// Sprint 11b.2 §2.4 — Edit a project's avatar.
//
// Live-preview WorkspaceAvatar (top), 2-character initials input, 12-color
// palette grid + Auto chip, Reset to auto, Save. Uses useProjectsStore
// for the optimistic write + multi-server broadcast (D-007 §2.5).

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useTranslation } from '../i18n';
import { useProjectsStore } from '../stores/projects.store';
import {
  PALETTE_IDS,
  type PaletteId,
  defaultInitials,
  paletteColor,
  autoColor,
} from '../lib/avatar-defaults';
import { WorkspaceAvatar } from './atoms/WorkspaceAvatar';
import type { Project } from '../types';

const INITIALS_REGEX = /^[A-Z0-9]{2}$/;

export interface EditProjectModalProps {
  project: Project | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ColorChoice = PaletteId | 'auto';

export function EditProjectModal({ project, open, onOpenChange }: EditProjectModalProps) {
  const { t } = useTranslation();
  const updateAvatar = useProjectsStore((s) => s.updateAvatar);

  const [initials, setInitials] = useState('');
  const [color, setColor] = useState<ColorChoice>('auto');
  const [submitting, setSubmitting] = useState(false);

  // Reseed form when the modal opens for a new project.
  useEffect(() => {
    if (!open || !project) return;
    setInitials(
      project.avatarInitials !== null
        ? project.avatarInitials
        : defaultInitials(project.name, project.id),
    );
    setColor(
      project.avatarColor !== null && (PALETTE_IDS as readonly string[]).includes(project.avatarColor)
        ? (project.avatarColor as PaletteId)
        : 'auto',
    );
    setSubmitting(false);
  }, [open, project]);

  // Live preview: same resolveAvatar logic the deployed avatar will use.
  // Hook MUST come before any conditional return — Rules of Hooks. The
  // initial mount of this component receives `project={null}` (the modal
  // is mounted unconditionally in WorkspaceColumn) and only acquires a
  // project on the first right-click → Edit. If `useMemo` lived below the
  // `if (!project) return null` guard, the second render would call one
  // more hook than the first and React would throw error #310.
  const previewProps = useMemo(
    () => ({
      slug: project?.id ?? '',
      name: project?.name ?? '',
      unreadCount: 0,
      isActive: false,
      avatarColor: color === 'auto' ? null : color,
      avatarInitials: initials.length === 2 ? initials : null,
      presentational: true as const,
    }),
    [color, initials, project?.id, project?.name],
  );

  if (!project) return null;

  const initialsValid = INITIALS_REGEX.test(initials);
  const baselineInitials =
    project.avatarInitials !== null
      ? project.avatarInitials
      : defaultInitials(project.name, project.id);
  const baselineColor: ColorChoice =
    project.avatarColor !== null && (PALETTE_IDS as readonly string[]).includes(project.avatarColor)
      ? (project.avatarColor as PaletteId)
      : 'auto';
  const dirty = initials !== baselineInitials || color !== baselineColor;
  const canSave = initialsValid && dirty && !submitting;

  function handleInitialsChange(raw: string) {
    // Filter to alphanumeric + uppercase to enforce the storage contract.
    const filtered = raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 2);
    setInitials(filtered);
  }

  function handleResetAuto() {
    setColor('auto');
    setInitials(defaultInitials(project!.name, project!.id));
  }

  async function handleSave() {
    if (!canSave || !project) return;
    setSubmitting(true);
    const autoInitials = defaultInitials(project.name, project.id);
    // Persist null when the user landed on the deterministic default —
    // that way the avatar follows future name changes without manual edits.
    const initialsToPersist = initials === autoInitials ? null : initials;
    const colorToPersist = color === 'auto' ? null : color;
    await updateAvatar(project.id, {
      avatarColor: colorToPersist,
      avatarInitials: initialsToPersist,
    });
    setSubmitting(false);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-project-modal">
        <DialogHeader>
          <DialogTitle>{t('workspaceCustomization.editTitle')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          <div className="flex justify-center">
            <WorkspaceAvatar {...previewProps} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-project-initials">
              {t('workspaceCustomization.initials')}
            </Label>
            <Input
              id="edit-project-initials"
              value={initials}
              onChange={(e) => handleInitialsChange(e.target.value)}
              maxLength={2}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              disabled={submitting}
              data-testid="edit-project-initials-input"
            />
            <p className="text-caption text-muted-foreground">
              {t('workspaceCustomization.initialsHint')}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <Label>{t('workspaceCustomization.color')}</Label>
            <div className="grid grid-cols-4 gap-2">
              {PALETTE_IDS.map((id) => {
                const swatch = paletteColor(id);
                const selected = color === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setColor(id)}
                    className="flex items-center justify-center transition-all duration-150"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 'var(--radius)',
                      backgroundColor: swatch.bg,
                      outline: selected ? '2px solid hsl(var(--action))' : 'none',
                      outlineOffset: selected ? '2px' : '0',
                    }}
                    aria-label={t('workspaceCustomization.colorOption', { n: id.slice(-2) })}
                    aria-pressed={selected}
                    data-testid={`edit-project-swatch-${id}`}
                    disabled={submitting}
                  />
                );
              })}
              <AutoSwatch
                slug={project.id}
                selected={color === 'auto'}
                onSelect={() => setColor('auto')}
                disabled={submitting}
                ariaLabel={t('workspaceCustomization.autoColor')}
                glyph={t('workspaceCustomization.autoGlyph')}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
          <Button
            variant="ghost"
            onClick={handleResetAuto}
            disabled={submitting}
            data-testid="edit-project-reset"
          >
            {t('workspaceCustomization.resetAuto')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            data-testid="edit-project-save"
          >
            {submitting
              ? t('workspaceCustomization.saving')
              : t('workspaceCustomization.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface AutoSwatchProps {
  slug: string;
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
  ariaLabel: string;
  glyph: string;
}

function AutoSwatch({ slug, selected, onSelect, disabled, ariaLabel, glyph }: AutoSwatchProps) {
  // Diagonal split: top-left half = the deterministic auto-hue for this
  // slug, bottom-right half = muted foreground. Communicates "auto =
  // follow the system, with whatever hue currently applies".
  const auto = autoColor(slug);
  return (
    <button
      type="button"
      onClick={onSelect}
      className="relative flex items-center justify-center transition-all duration-150 overflow-hidden"
      style={{
        width: 40,
        height: 40,
        borderRadius: 'var(--radius)',
        outline: selected ? '2px solid hsl(var(--action))' : 'none',
        outlineOffset: selected ? '2px' : '0',
      }}
      aria-label={ariaLabel}
      aria-pressed={selected}
      data-testid="edit-project-swatch-auto"
      disabled={disabled}
    >
      <span
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, ${auto.bg} 0%, ${auto.bg} 50%, hsl(var(--muted)) 50%, hsl(var(--muted)) 100%)`,
        }}
        aria-hidden
      />
      <span className="relative text-caption font-550 text-white drop-shadow-sm">{glyph}</span>
    </button>
  );
}
